import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Toggle from '@/components/ui/Toggle';
import { ApiError } from '@/lib/api';
import { onboardingApi, type PartnerFull } from '@/lib/onboarding';
import { Field, Section } from './fields';
import { areaCls, btnGhost, btnPrimary, inputCls, numOrNull } from './styles';

interface Props {
  partner: PartnerFull;
  onSaved: (p: PartnerFull) => void;
  onToast: (message: string, tone?: 'success' | 'error') => void;
}

type Form = Record<string, string | boolean>;

const text = (v: string | number | null | undefined) => (v == null ? '' : String(v));

export default function CabinetStep({ partner, onSaved, onToast }: Props) {
  const { t } = useTranslation();
  const [f, setF] = useState<Form>({});
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState({ email: partner.email ?? '', fullName: partner.contact ?? '' });
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  useEffect(() => {
    setF({
      name: partner.name, shortName: text(partner.shortName), trn: text(partner.trn), address: text(partner.address), websiteUrl: text(partner.websiteUrl), categories: (partner.categories || []).join(', '),
      contact: text(partner.contact), role: text(partner.role), phone: text(partner.phone), whatsapp: text(partner.whatsapp), email: text(partner.email), city: text(partner.city), warehouse: text(partner.warehouse), pickupHours: text(partner.pickupHours),
      discount: text(partner.discount), noDiscountOnSpecial: partner.noDiscountOnSpecial, markup: text(partner.markup), markupB2b: text(partner.markupB2b), minResaleType: partner.minResaleType || 'none', minResaleValue: text(partner.minResaleValue),
      reservationPeriodDays: text(partner.reservationPeriodDays), reservationRespondHours: text(partner.reservationRespondHours), poConfirmHours: text(partner.poConfirmHours), paymentLagDays: text(partner.paymentLagDays), warrantyMonths: text(partner.warrantyMonths),
      deliveryDaysMin: text(partner.deliveryDaysMin), deliveryDaysMax: text(partner.deliveryDaysMax), brandVisible: partner.brandVisible, status: partner.status, notes: text(partner.notes),
    });
  }, [partner]);

  const s = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF((x) => ({ ...x, [k]: e.target.value }));
  const v = (k: string) => String(f[k] ?? '');

  const save = async () => {
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        name: v('name'), shortName: v('shortName') || null, trn: v('trn') || null, address: v('address') || null, websiteUrl: v('websiteUrl') || null,
        categories: v('categories').split(',').map((x) => x.trim()).filter(Boolean),
        contact: v('contact') || null, role: v('role') || null, phone: v('phone') || null, whatsapp: v('whatsapp') || null, email: v('email') || null, city: v('city') || null, warehouse: v('warehouse') || null, pickupHours: v('pickupHours') || null,
        discount: Number(v('discount') || 0), noDiscountOnSpecial: Boolean(f.noDiscountOnSpecial), markup: numOrNull(v('markup')), markupB2b: numOrNull(v('markupB2b')), minResaleType: v('minResaleType'), minResaleValue: numOrNull(v('minResaleValue')),
        reservationPeriodDays: Number(v('reservationPeriodDays') || 7), reservationRespondHours: Number(v('reservationRespondHours') || 24), poConfirmHours: Number(v('poConfirmHours') || 24), paymentLagDays: Number(v('paymentLagDays') || 3), warrantyMonths: numOrNull(v('warrantyMonths')),
        deliveryDaysMin: numOrNull(v('deliveryDaysMin')), deliveryDaysMax: numOrNull(v('deliveryDaysMax')), brandVisible: Boolean(f.brandVisible), status: v('status'), notes: v('notes') || null,
      };
      const p = await onboardingApi.patchPartner(partner.id, body);
      onSaved(p);
      onToast(t('onb.saved'));
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async () => {
    if (!invite.email) return;
    try {
      const r = await onboardingApi.inviteOwner(partner.id, { email: invite.email, fullName: invite.fullName || undefined });
      setInviteUrl(r.inviteUrl);
      onToast(t('onb.invited', { email: invite.email }));
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    }
  };

  const uploadLogo = async (file: File | undefined) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await onboardingApi.uploadLogo(partner.id, fd);
      onSaved(await onboardingApi.partner(partner.id));
      onToast(t('onb.saved'));
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    }
  };

  const sel = (k: string, opts: [string, string][]) => (
    <select className={inputCls} value={v(k)} onChange={s(k)}>
      {opts.map(([val, label]) => (
        <option key={val} value={val}>
          {label}
        </option>
      ))}
    </select>
  );

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <div className="flex flex-col gap-4 xl:col-span-2">
        <Section title={t('onb.sectionCompany')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={`${t('onb.name')} *`} className="sm:col-span-2"><input className={inputCls} value={v('name')} onChange={s('name')} /></Field>
            <Field label={t('onb.shortName')}><input className={inputCls} value={v('shortName')} onChange={s('shortName')} /></Field>
            <Field label={t('onb.trn')}><input className={`${inputCls} tnum`} value={v('trn')} onChange={s('trn')} /></Field>
            <Field label={t('onb.address')} className="sm:col-span-2"><input className={inputCls} value={v('address')} onChange={s('address')} /></Field>
            <Field label={t('onb.websiteUrl')}><input className={inputCls} value={v('websiteUrl')} onChange={s('websiteUrl')} /></Field>
            <Field label={t('onb.categories')}><input className={inputCls} value={v('categories')} onChange={s('categories')} placeholder="chairs, desks" /></Field>
            <Field label={t('onb.statusLabel')}>{sel('status', [['Onboarding', t('status.Onboarding')], ['Negotiating', t('status.Negotiating')], ['Active', t('status.Active')], ['Paused', t('status.Paused')]])}</Field>
          </div>
        </Section>
        <Section title={t('onb.sectionContacts')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('onb.contact')}><input className={inputCls} value={v('contact')} onChange={s('contact')} /></Field>
            <Field label={t('onb.role')}><input className={inputCls} value={v('role')} onChange={s('role')} /></Field>
            <Field label={t('onb.phone')}><input className={inputCls} value={v('phone')} onChange={s('phone')} /></Field>
            <Field label={t('onb.whatsapp')}><input className={inputCls} value={v('whatsapp')} onChange={s('whatsapp')} /></Field>
            <Field label={t('onb.email')}><input className={inputCls} value={v('email')} onChange={s('email')} /></Field>
          </div>
        </Section>
        <Section title={t('onb.sectionWarehouse')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('onb.city')}><input className={inputCls} value={v('city')} onChange={s('city')} /></Field>
            <Field label={t('onb.pickupHours')}><input className={inputCls} value={v('pickupHours')} onChange={s('pickupHours')} /></Field>
            <Field label={t('onb.warehouse')} className="sm:col-span-2"><input className={inputCls} value={v('warehouse')} onChange={s('warehouse')} /></Field>
          </div>
        </Section>
        <Section title={t('onb.sectionTerms')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label={t('onb.discount')}><input className={`${inputCls} tnum`} type="number" min={0} max={100} value={v('discount')} onChange={s('discount')} /></Field>
            <Field label={t('onb.markup')}><input className={`${inputCls} tnum`} type="number" min={0} max={500} value={v('markup')} onChange={s('markup')} /></Field>
            <Field label={t('onb.markupB2b')}><input className={`${inputCls} tnum`} type="number" min={0} max={500} value={v('markupB2b')} onChange={s('markupB2b')} /></Field>
            <Field label={t('onb.minResaleType')}>{sel('minResaleType', [['none', t('onb.minResale.none')], ['partner_price', t('onb.minResale.partner_price')], ['markup_over_cost', t('onb.minResale.markup_over_cost')], ['per_sku', t('onb.minResale.per_sku')]])}</Field>
            <Field label={t('onb.minResaleValue')}><input className={`${inputCls} tnum`} type="number" value={v('minResaleValue')} onChange={s('minResaleValue')} /></Field>
            <Field label={t('onb.warrantyMonths')}><input className={`${inputCls} tnum`} type="number" min={0} value={v('warrantyMonths')} onChange={s('warrantyMonths')} /></Field>
            <Field label={t('onb.reservationPeriodDays')}><input className={`${inputCls} tnum`} type="number" min={1} value={v('reservationPeriodDays')} onChange={s('reservationPeriodDays')} /></Field>
            <Field label={t('onb.reservationRespondHours')}><input className={`${inputCls} tnum`} type="number" min={1} value={v('reservationRespondHours')} onChange={s('reservationRespondHours')} /></Field>
            <Field label={t('onb.poConfirmHours')}><input className={`${inputCls} tnum`} type="number" min={1} value={v('poConfirmHours')} onChange={s('poConfirmHours')} /></Field>
            <Field label={t('onb.paymentLagDays')}><input className={`${inputCls} tnum`} type="number" min={0} value={v('paymentLagDays')} onChange={s('paymentLagDays')} /></Field>
          </div>
          <div className="mt-4 space-y-3 border-t border-background-200 pt-4">
            <Toggle checked={Boolean(f.noDiscountOnSpecial)} onChange={(x) => setF((y) => ({ ...y, noDiscountOnSpecial: x }))} label={t('onb.noDiscountOnSpecial')} />
          </div>
        </Section>
        <Section title={t('onb.sectionSite')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('onb.deliveryDays')}>
              <div className="flex items-center gap-2">
                <input className={`${inputCls} tnum`} type="number" min={0} value={v('deliveryDaysMin')} onChange={s('deliveryDaysMin')} />
                <span className="text-foreground-400">–</span>
                <input className={`${inputCls} tnum`} type="number" min={0} value={v('deliveryDaysMax')} onChange={s('deliveryDaysMax')} />
              </div>
            </Field>
            <div className="pt-5">
              <Toggle checked={Boolean(f.brandVisible)} onChange={(x) => setF((y) => ({ ...y, brandVisible: x }))} label={t('onb.brandVisible')} description={t('onb.brandVisibleDesc')} />
            </div>
            <Field label={t('onb.notes')} className="sm:col-span-2"><textarea className={areaCls} rows={3} value={v('notes')} onChange={s('notes')} /></Field>
          </div>
        </Section>
        <div className="flex justify-end">
          <button type="button" onClick={save} disabled={busy || !v('name').trim()} className={btnPrimary}>
            <i className="ri-save-3-line text-[14px]" /> {busy ? t('onb.saving') : t('onb.save')}
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <Section title={t('onb.logo')}>
          <div className="flex items-center gap-4">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md bg-secondary-100 text-[15px] font-semibold text-secondary-800">
              {partner.logoPath ? <img src={`/api/partners/${partner.id}/logo?v=${encodeURIComponent(partner.logoPath)}`} alt="" className="h-full w-full object-contain" /> : (partner.shortName || partner.name).slice(0, 2).toUpperCase()}
            </span>
            <label className={btnGhost}>
              <i className="ri-camera-line text-[14px]" /> {t('onb.uploadLogo')}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void uploadLogo(e.target.files?.[0])} />
            </label>
          </div>
        </Section>
        <Section title={t('onb.inviteOwner')}>
          <div className="flex flex-col gap-3">
            <Field label={t('onb.inviteEmail')}><input className={inputCls} type="email" value={invite.email} onChange={(e) => setInvite((x) => ({ ...x, email: e.target.value }))} /></Field>
            <Field label={t('onb.inviteName')}><input className={inputCls} value={invite.fullName} onChange={(e) => setInvite((x) => ({ ...x, fullName: e.target.value }))} /></Field>
            <button type="button" onClick={sendInvite} disabled={!invite.email.includes('@')} className={btnGhost}>
              <i className="ri-mail-send-line text-[14px]" /> {t('onb.invite')}
            </button>
            {inviteUrl && (
              <p className="break-all text-[11px] text-foreground-500">
                {t('onb.inviteLink')} <code className="text-foreground-800">{inviteUrl}</code>
              </p>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}
